import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Lock, Moon, Sun, Eye, EyeOff } from 'lucide-react';
import { API_PATH, getToken, request } from '../config/api';
import { useAuth } from '../store/AuthContext';
import { useTheme } from '../store/ThemeContext';
import Button from '../components/ui/Button';
import Toggle from '../components/ui/Toggle';
import PasswordStrengthMeter from '../components/ui/PasswordStrengthMeter';
import PageHeroHeader from '../components/ui/PageHeroHeader';
import styles from './SettingsPage.module.css';

const getInitials = (name) =>
    (name || '').trim().split(/\s+/).map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '?';

const ROLE_LABELS = { superadmin: 'Superadmin', admin: 'Admin', pending: 'Pending approval' };

const SettingsPage = () => {
    const navigate = useNavigate();
    const { user, login, refreshUser } = useAuth();
    const { isDark, toggleTheme } = useTheme();

    const [name, setName] = useState(user?.name ?? '');
    const [mobile, setMobile] = useState(user?.mobile ?? '');
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // SSO signups (Google/Microsoft) get a random password behind the scenes that they never
    // see — they can't possibly supply a "current password" to change it. hasPassword===false
    // means they haven't set a real one yet, so this form collects only a new password and
    // posts to /auth/set-password instead of /auth/change-password.
    const hasPassword = user?.hasPassword !== false;

    useEffect(() => {
        if (user?.name != null) setName(user.name);
        if (user?.mobile != null) setMobile(user.mobile);
    }, [user?.name, user?.mobile]);

    useEffect(() => {
        const token = getToken();
        if (!token) {
            navigate('/login');
            return;
        }
    }, [navigate]);


    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setProfileMessage({ type: '', text: '' });
        const trimmedName = name.trim();
        if (trimmedName.length < 2) {
            setProfileMessage({ type: 'error', text: 'Name must be at least 2 characters.' });
            return;
        }
        const mobileNorm = mobile.replace(/\D/g, '').trim();
        if (mobileNorm && mobileNorm.length !== 10) {
            setProfileMessage({ type: 'error', text: 'Mobile must be 10 digits.' });
            return;
        }
        setProfileSaving(true);
        try {
            const res = await request(`${API_PATH}/auth/profile`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: trimmedName, mobile: mobileNorm || null }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setProfileMessage({ type: 'error', text: data.error || 'Failed to update profile.' });
                return;
            }
            const rememberMe = !!localStorage.getItem('token');
            if (data.token) {
                login(data.token, rememberMe, data.user);
            } else {
                refreshUser();
            }
            setProfileMessage({ type: 'success', text: 'Profile updated.' });
        } catch {
            setProfileMessage({ type: 'error', text: 'Network error. Try again.' });
        } finally {
            setProfileSaving(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordMessage({ type: '', text: '' });
        if (hasPassword && !currentPassword) {
            setPasswordMessage({ type: 'error', text: 'Enter your current password.' });
            return;
        }
        if (newPassword.length < 8) {
            setPasswordMessage({ type: 'error', text: 'New password must be at least 8 characters.' });
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'New password and confirmation do not match.' });
            return;
        }
        setPasswordSaving(true);
        try {
            const endpoint = hasPassword ? 'change-password' : 'set-password';
            const body = hasPassword ? { currentPassword, newPassword } : { newPassword };
            const res = await request(`${API_PATH}/auth/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({}));
            if (res.status === 401) {
                setPasswordMessage({ type: 'error', text: data.error || 'Current password is incorrect.' });
                return;
            }
            if (!res.ok) {
                setPasswordMessage({ type: 'error', text: data.error || `Failed to ${hasPassword ? 'change' : 'set'} password.` });
                return;
            }
            // Changing/setting the password bumps the account's server-side pw_version, which
            // invalidates every token issued before this change — including the one this
            // request itself just used. Save the freshly-issued token so the current session
            // keeps working instead of the next request getting logged out unexpectedly. Also
            // carries the updated hasPassword: true so this form switches to "Change password"
            // (current-password-required) immediately, without waiting for a refresh.
            if (data.token) {
                const rememberMe = !!localStorage.getItem('token');
                login(data.token, rememberMe, { ...user, hasPassword: true });
            }
            setPasswordMessage({ type: 'success', text: hasPassword ? 'Password updated successfully.' : 'Password set successfully. You can now also sign in with your email and this password.' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch {
            setPasswordMessage({ type: 'error', text: 'Network error. Try again.' });
        } finally {
            setPasswordSaving(false);
        }
    };

    if (!user) return null;

    return (
        <div className={`${styles.page} ${styles.pageWide}`}>
            <PageHeroHeader
                icon={User}
                eyebrow="Account"
                title="Settings"
                badge={<span className={`${styles.roleBadge} ${user.role === 'superadmin' ? styles.roleBadgeSuperadmin : ''}`}>{ROLE_LABELS[user.role] || user.role}</span>}
                subtitle="Manage your profile, appearance, and security."
                actions={<div className={styles.settingsHeroIdentity}><div className={styles.settingsHeroAvatar} aria-hidden>{getInitials(user.name)}</div><div><strong>{user.name}</strong><span>{user.email}</span></div></div>}
            />

            <div className={styles.gridStack}>
                <div className={styles.gridRow}>
                {/* Password — left */}
                <section className={styles.card} aria-labelledby="password-heading">
                    <div className={styles.cardHead}>
                        <span className={styles.cardIcon} aria-hidden>
                            <Lock size={20} />
                        </span>
                        <div>
                            <h2 id="password-heading" className={styles.cardTitle}>{hasPassword ? 'Change password' : 'Set a password'}</h2>
                            <p className={styles.cardDesc}>
                                {hasPassword
                                    ? 'Update your password securely'
                                    : 'You signed up with Google or Microsoft — set a password to also sign in with your email'}
                            </p>
                        </div>
                    </div>
                    <form onSubmit={handlePasswordSubmit} className={styles.form}>
                        {hasPassword && (
                            <div className={styles.field}>
                                <label htmlFor="current-password" className={styles.label}>Current password</label>
                                <div className={styles.inputWrapper}>
                                    <Lock size={16} className={styles.inputIcon} aria-hidden />
                                    <input
                                        id="current-password"
                                        type={showCurrentPassword ? 'text' : 'password'}
                                        className={`${styles.input} ${styles.inputWithIcon} ${styles.inputWithToggle}`}
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        placeholder="Enter current password"
                                        disabled={passwordSaving}
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        className={styles.eyeBtn}
                                        onClick={() => setShowCurrentPassword((v) => !v)}
                                        tabIndex={-1}
                                        aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showCurrentPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className={styles.formGrid}>
                            <div className={styles.field}>
                                <label htmlFor="new-password" className={styles.label}>New password</label>
                                <div className={styles.inputWrapper}>
                                    <Lock size={16} className={styles.inputIcon} aria-hidden />
                                    <input
                                        id="new-password"
                                        type={showNewPassword ? 'text' : 'password'}
                                        className={`${styles.input} ${styles.inputWithIcon} ${styles.inputWithToggle}`}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Min 8 characters"
                                        disabled={passwordSaving}
                                        autoComplete="new-password"
                                        minLength={8}
                                    />
                                    <button
                                        type="button"
                                        className={styles.eyeBtn}
                                        onClick={() => setShowNewPassword((v) => !v)}
                                        tabIndex={-1}
                                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showNewPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                                    </button>
                                </div>
                                <PasswordStrengthMeter password={newPassword} />
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="confirm-password" className={styles.label}>Confirm</label>
                                <div className={styles.inputWrapper}>
                                    <Lock size={16} className={styles.inputIcon} aria-hidden />
                                    <input
                                        id="confirm-password"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        className={`${styles.input} ${styles.inputWithIcon} ${styles.inputWithToggle}`}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Re-enter new password"
                                        disabled={passwordSaving}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className={styles.eyeBtn}
                                        onClick={() => setShowConfirmPassword((v) => !v)}
                                        tabIndex={-1}
                                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                                    </button>
                                </div>
                            </div>
                        </div>
                        {passwordMessage.text && (
                            <div className={passwordMessage.type === 'success' ? styles.alertSuccess : styles.alertError} role="alert">
                                {passwordMessage.text}
                            </div>
                        )}
                        <Button type="submit" variant="primary" size="sm" disabled={passwordSaving} className={`${styles.formSubmit} ${styles.pillBtn}`}>
                            {passwordSaving ? (hasPassword ? 'Updating…' : 'Setting…') : (hasPassword ? 'Change password' : 'Set password')}
                        </Button>
                    </form>
                </section>

                {/* Profile — right */}
                <section className={styles.card} aria-labelledby="profile-heading">
                    <div className={styles.cardHead}>
                        <span className={styles.cardIcon} aria-hidden>
                            <User size={20} />
                        </span>
                        <div>
                            <h2 id="profile-heading" className={styles.cardTitle}>Profile</h2>
                            <p className={styles.cardDesc}>Your name and email</p>
                        </div>
                    </div>
                    <form onSubmit={handleProfileSubmit} className={styles.form}>
                        <div className={styles.formGrid}>
                            <div className={styles.field}>
                                <label htmlFor="settings-name" className={styles.label}>Name</label>
                                <div className={styles.inputWrapper}>
                                    <User size={16} className={styles.inputIcon} aria-hidden />
                                    <input
                                        id="settings-name"
                                        type="text"
                                        className={`${styles.input} ${styles.inputWithIcon}`}
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Your name"
                                        disabled={profileSaving}
                                        minLength={2}
                                    />
                                </div>
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="settings-email" className={styles.label}>Email</label>
                                <div className={styles.inputWrapper}>
                                    <Mail size={16} className={styles.inputIcon} aria-hidden />
                                    <input
                                        id="settings-email"
                                        type="email"
                                        className={`${styles.input} ${styles.inputWithIcon} ${styles.inputReadOnly}`}
                                        value={user.email ?? ''}
                                        readOnly
                                        disabled
                                        aria-readonly
                                    />
                                </div>
                                <p className={styles.hint}>Email cannot be changed</p>
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="settings-mobile" className={styles.label}>Mobile number</label>
                            <div className={styles.inputWrapper}>
                                <Phone size={16} className={styles.inputIcon} aria-hidden />
                                <input
                                    id="settings-mobile"
                                    type="tel"
                                    className={`${styles.input} ${styles.inputWithIcon}`}
                                    value={mobile}
                                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                    placeholder="10-digit number"
                                    disabled={profileSaving}
                                    autoComplete="tel"
                                    maxLength={10}
                                />
                            </div>
                            <p className={styles.hint}>Optional. 10 digits.</p>
                        </div>
                        {profileMessage.text && (
                            <div className={profileMessage.type === 'success' ? styles.alertSuccess : styles.alertError} role="alert">
                                {profileMessage.text}
                            </div>
                        )}
                        <Button type="submit" variant="primary" size="sm" disabled={profileSaving} className={`${styles.formSubmit} ${styles.pillBtn}`}>
                            {profileSaving ? 'Saving…' : 'Save profile'}
                        </Button>
                    </form>
                </section>
                </div>

                {/* Appearance — its own full-width row below */}
                <section className={styles.card} aria-labelledby="appearance-heading">
                    <div className={styles.cardHead}>
                        <span className={styles.cardIcon} aria-hidden>
                            {isDark ? <Moon size={20} /> : <Sun size={20} />}
                        </span>
                        <div>
                            <h2 id="appearance-heading" className={styles.cardTitle}>Appearance</h2>
                            <p className={styles.cardDesc}>Choose light or dark theme</p>
                        </div>
                    </div>
                    <div className={styles.themeRow}>
                        <span className={styles.themeLabel}>{isDark ? 'Dark' : 'Light'} mode</span>
                        <Toggle
                            id="settings-theme"
                            checked={isDark}
                            onChange={toggleTheme}
                            label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        />
                    </div>
                </section>
            </div>
        </div>
    );
};

export default SettingsPage;
